import { useEffect, useMemo, useRef, useState } from 'react';
import { COUNTRIES, DEFAULT_COUNTRY, normalizePhone, parsePhone, validateNational, phoneLengthIssue, type Country } from '../lib/phone';

interface PhoneInputProps {
  // Canonical stored value (IL → `05XXXXXXXX`, others → E.164). The component
  // re-derives flag + digits from this on mount / external change.
  value: string;
  onChange: (normalized: string, valid: boolean) => void;
  // Visual style coming from the campaign form (fsInput) — applied to the
  // combined box so the phone field matches the other inputs 1:1.
  style?: React.CSSProperties;
  placeholder?: string;
  dir?: 'rtl' | 'ltr';
  required?: boolean;
}

// International phone field: flag + dial-code dropdown next to a national-number
// input, with per-country length/format validation. Emits a normalized value so
// downstream (receipts, Bit SMS) always get a clean number — the #1 source of
// failed Bit charges was donors typing "+972-50…" or "972…" or a landline.
export default function PhoneInput({ value, onChange, style, placeholder, dir = 'rtl', required }: PhoneInputProps) {
  const initial = useMemo(() => parsePhone(value), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [country, setCountry] = useState<Country>(initial.country || DEFAULT_COUNTRY);
  const [national, setNational] = useState<string>(initial.national || '');
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  // Remember the last value we emitted so the sync-effect below can tell our
  // own echo (parent re-passing it as `value`) apart from a genuine external
  // reset — otherwise it fights the user mid-typing (a lone leading "0" would
  // normalize to "" and get wiped before the next digit).
  const lastEmitted = useRef<string>(normalizePhone(initial.country || DEFAULT_COUNTRY, initial.national || ''));
  const en = dir === 'ltr';

  // Keep internal state in sync only when the parent resets the value
  // externally (a "clear form" or a prefilled donor record arriving late) —
  // never on our own echo.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    const parsed = parsePhone(value);
    setCountry(parsed.country || DEFAULT_COUNTRY);
    setNational(parsed.national || '');
    lastEmitted.current = normalizePhone(parsed.country || DEFAULT_COUNTRY, parsed.national || '');
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const emit = (c: Country, nat: string) => {
    const norm = normalizePhone(c, nat);
    lastEmitted.current = norm;
    onChange(norm, validateNational(c, nat));
  };

  const pickCountry = (c: Country) => {
    setCountry(c);
    setOpen(false);
    setSearch('');
    emit(c, national);
  };

  const onDigits = (raw: string) => {
    // allow digits, spaces and dashes while typing; normalize on emit.
    const cleaned = raw.replace(/[^\d\s()+-]/g, '');
    setNational(cleaned);
    emit(country, cleaned);
  };

  const valid = !national ? !required : validateNational(country, national);
  const showError = touched && !!national && !valid;

  // Decompose the incoming input style so the OUTER box owns the border/bg and
  // the inner controls stay borderless — gives one seamless field.
  const s = style || {};
  const boxStyle: React.CSSProperties = {
    ...s,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    boxSizing: 'border-box',
    // Phone numbers are inherently left-to-right — force the field itself to
    // LTR even inside a Hebrew/RTL form, so the flag + dial code sit on the
    // LEFT and digits type left-to-right (matches how people read a phone
    // number). Only the surrounding label/error follow the form's language.
    direction: 'ltr',
    textAlign: 'left',
    borderColor: showError ? '#dc2626' : (s as any).borderColor,
    ...(showError ? { border: '1px solid #dc2626' } : {}),
  };

  const filtered = search.trim()
    ? COUNTRIES.filter(c =>
        c.name.includes(search) ||
        c.nameEn.toLowerCase().includes(search.toLowerCase()) ||
        c.dial.includes(search.replace(/\D/g, '')))
    : COUNTRIES;

  return (
    <div ref={rootRef} style={{ position: 'relative', width: '100%' }} dir={dir}>
      <div style={boxStyle}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            border: 'none', background: 'transparent', cursor: 'pointer',
            padding: 0, font: 'inherit', color: 'inherit', whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          aria-label="country code"
        >
          <span style={{ fontSize: '1.15em', lineHeight: 1 }}>{country.flag}</span>
          <span style={{ opacity: 0.8, direction: 'ltr' as const }}>+{country.dial}</span>
          <span style={{ opacity: 0.5, fontSize: '0.7em' }}>▾</span>
        </button>
        <input
          type="tel"
          inputMode="tel"
          dir="ltr"
          // The dial code (+972) is shown separately, so the placeholder is the
          // NATIONAL part — strip a leading trunk 0 (default "050-0000000" →
          // "50-0000000") so it reads correctly as E.164 next to the prefix.
          placeholder={(placeholder || '').replace(/^0(?=\d)/, '') || (en ? 'Phone number' : 'מספר טלפון')}
          value={national}
          onChange={e => onDigits(e.target.value)}
          onBlur={() => setTouched(true)}
          style={{
            flex: 1, minWidth: 0, border: 'none', outline: 'none',
            background: 'transparent', padding: 0, font: 'inherit', color: 'inherit',
            textAlign: 'left',
          }}
        />
      </div>

      {showError && (
        <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
          {(() => {
            // Prefer a specific digit-count message ("too short / too long for
            // <country>") so the donor knows exactly what's wrong; fall back to
            // a generic invalid-number message for a wrong prefix/pattern.
            const issue = phoneLengthIssue(country, national);
            const cn = en ? country.nameEn : country.name;
            if (issue === 'short') return en ? `Number is too short for ${cn}` : `המספר קצר מדי עבור ${cn}`;
            if (issue === 'long') return en ? `Number is too long for ${cn}` : `המספר ארוך מדי עבור ${cn}`;
            if (country.code === 'IL') return en ? 'Enter a valid Israeli number (e.g. 050-1234567)' : 'יש להזין מספר טלפון ישראלי תקין (לדוגמה 050-1234567)';
            return en ? `Enter a valid phone number for ${cn}` : `יש להזין מספר טלפון תקין עבור ${cn}`;
          })()}
        </div>
      )}

      {open && (
        <div
          style={{
            position: 'absolute', top: '100%', insetInlineStart: 'auto', left: 0, marginTop: 4,
            width: 'max(260px, 100%)', maxHeight: 280, overflowY: 'auto',
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 1000,
          }}
        >
          <div style={{ padding: 8, position: 'sticky', top: 0, background: '#fff', borderBottom: '1px solid #f1f5f9' }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={en ? 'Search country…' : 'חיפוש מדינה…'}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '6px 10px',
                border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, outline: 'none',
              }}
            />
          </div>
          {filtered.map(c => (
            <button
              key={c.code + c.dial}
              type="button"
              onClick={() => pickCountry(c)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '8px 12px', border: 'none', background: c.code === country.code ? '#eff6ff' : '#fff',
                cursor: 'pointer', textAlign: en ? 'left' : 'right', font: 'inherit',
              }}
            >
              <span style={{ fontSize: '1.2em' }}>{c.flag}</span>
              <span style={{ flex: 1 }}>{en ? c.nameEn : c.name}</span>
              <span style={{ opacity: 0.6, direction: 'ltr' as const }}>+{c.dial}</span>
            </button>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: 12, color: '#9ca3af', fontSize: 14, textAlign: 'center' }}>
              {en ? 'No matches' : 'אין תוצאות'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
